import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LOGO } from "./Navbar";
import { IS_SNAP } from "../seo/snap";

/* -------------------------------------------------------------------------- */
/*  SETTINGS                                                                  */
/* -------------------------------------------------------------------------- */

// true  -> the loader also plays (shorter) every time the route changes
// false -> it only plays once, when the site first opens, on whichever page
//          the visitor lands on
const SHOW_ON_NAVIGATION = true;

const FIRST_MIN_MS = 2200; // minimum time on first load
const NAV_MIN_MS = 900; // minimum time between pages
const MAX_WAIT_MS = 5000; // never wait longer than this for images

/* -------------------------------------------------------------------------- */
/*  HELPERS                                                                   */
/* -------------------------------------------------------------------------- */

function preloadImages(urls) {
  return Promise.all(
    urls.map(
      (url) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = img.onerror = resolve;
          img.src = url;
        })
    )
  );
}

/* -------------------------------------------------------------------------- */
/*  HOOK                                                                      */
/*  Call it once in App.js. It returns whether the loader should be on screen */
/*  and keeps the page from scrolling while it is.                            */
/* -------------------------------------------------------------------------- */

export function useRouteLoader(pathname, preload = []) {
  // The path the loader has finished for. `null` means the first load is
  // still in progress. Deriving `loading` from this (instead of setting it in
  // an effect) means the loader covers the new page on the very same render,
  // so there is no one-frame flash of the page before it appears.
  const [donePath, setDonePath] = useState(null);

  const first = donePath === null;
  // During the pre-render snapshot the loader never shows, so the snapshot
  // can never save `overflow: hidden` on the body.
  const loading = !IS_SNAP && (first || (SHOW_ON_NAVIGATION && donePath !== pathname));

  useEffect(() => {
    if (IS_SNAP) return;
    if (!first && !SHOW_ON_NAVIGATION) return;

    let cancelled = false;
    let timer;
    const started = Date.now();
    const minMs = first ? FIRST_MIN_MS : NAV_MIN_MS;
    const assets = first ? [LOGO, ...preload] : [];
    const giveUp = new Promise((resolve) => setTimeout(resolve, MAX_WAIT_MS));

    Promise.race([preloadImages(assets), giveUp]).then(() => {
      if (cancelled) return;
      const wait = Math.max(0, minMs - (Date.now() - started));
      timer = setTimeout(() => {
        if (!cancelled) setDonePath(pathname);
      }, wait);
    });

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `first` and `preload` are intentionally left out: this should run once
    // per route change, not every time the loader state flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // no scrolling behind the loader
  useEffect(() => {
    if (!loading) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [loading]);

  return { loading, first };
}

/* -------------------------------------------------------------------------- */
/*  LOADER  (water rises inside the logo, then the screen lifts away)         */
/*  `quick` is the shorter version used when moving between pages.            */
/* -------------------------------------------------------------------------- */

export default function Loader({ quick = false }) {
  return (
    <motion.div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white"
      initial={{ y: 0 }}
      exit={{ y: "-100%" }}
      transition={{ duration: quick ? 0.6 : 0.9, ease: [0.76, 0, 0.24, 1] }}
      role="status"
      aria-label="Loading MajiMaji Hub"
    >
      <div className="relative w-56 sm:w-72">
        <img src={LOGO} alt="" className="w-full opacity-[0.12]" />
        <motion.img
          src={LOGO}
          alt="MajiMaji Hub"
          className="absolute inset-0 w-full"
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: quick ? 0.7 : 1.9, ease: [0.65, 0, 0.35, 1] }}
        />
      </div>
      <div className="mt-8 h-[2px] w-40 overflow-hidden bg-[#0A3568]/10">
        <motion.div
          className="h-full origin-left bg-[#0A3568]"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: quick ? 0.8 : 2, ease: "easeInOut" }}
        />
      </div>
    </motion.div>
  );
}